class AriaHandler {
	init() {
		const addClickableListeners = (el: HTMLElement) => {
			el.addEventListener('click', (e: MouseEvent) => {
				const clicked = e.target as HTMLElement;
				let target = null;
				if (clicked.hasAttribute('aria-controls')) {
					target = e.target as HTMLElement;
				} else {
					target = clicked.closest('button[aria-controls]') as HTMLElement;
				}
				if (null !== target) {
					const targetExpanded =
						'true' === target.getAttribute('aria-expanded') ? 'false' : 'true';
					this.handleChangeAriaControls(target, targetExpanded);
				}
			});
		};

		const addFocusHoverableListeners = (el: HTMLElement) => {
			el.addEventListener('focusin', (e: FocusEvent) => {
				if (1200 <= window.innerWidth) {
					this.handleChangeAriaControls(e.currentTarget as HTMLElement, 'true');
				}
			});
			el.addEventListener('focusout', (e: FocusEvent) => {
				if (1200 <= window.innerWidth && !el.contains(e.relatedTarget as HTMLElement)) {
					this.handleChangeAriaControls(e.currentTarget as HTMLElement, 'false');
				}
			});

			el.addEventListener('mouseenter', (e: MouseEvent) => {
				if (1200 <= window.innerWidth) {
					this.handleChangeAriaControls(e.target as HTMLElement, 'true');
				}
			});
			el.addEventListener('mouseleave', (e: MouseEvent) => {
				if (1200 <= window.innerWidth) {
					this.handleChangeAriaControls(e.target as HTMLElement, 'false');
				}
			});
		};

		const clickableAriaElements = document.querySelectorAll(
			'button[aria-controls]:not(.exclude-aria-handler)'
		) as NodeListOf<HTMLElement>;
		clickableAriaElements.forEach((el) => {
			addClickableListeners(el);
		});

		const focusHoverableAriaElements = document.querySelectorAll(
			'li[aria-controls]'
		) as NodeListOf<HTMLElement>;
		focusHoverableAriaElements.forEach((el) => {
			addFocusHoverableListeners(el);
		});
	}

	// Set aria-expanded on target and elements controlled by target.
	handleChangeAriaControls = (target: HTMLElement, targetExpanded: string) => {
		if (target) {
			let allControllers = null;
			const controlId = target.getAttribute('aria-controls');
			if (controlId) {
				const controlledElement = document.getElementById(controlId);
				if (controlledElement) {
					controlledElement.setAttribute('aria-expanded', targetExpanded);
					if ('true' === targetExpanded) {
						controlledElement.removeAttribute('aria-hidden');

						// Find previously hidden elements within expanded (like close buttons in modals) and make sure they are not hidden.
						const hiddenElements = controlledElement.querySelectorAll(
							'[aria-hidden="true"]'
						) as NodeListOf<HTMLElement>;
						hiddenElements.forEach((el) => {
							if (!this.elIsHidden(el)) {
								el.removeAttribute('aria-hidden');
							}
						});
					}

					if (
						controlledElement.hasAttribute('aria-modal') &&
						'true' === controlledElement.getAttribute('aria-modal')
					) {
						this.trapFocus(controlledElement);
					}
				}
				allControllers = document.querySelectorAll(
					'[aria-controls="' + controlId + '"]'
				) as NodeListOf<HTMLElement>;
				allControllers.forEach((el) => {
					el.setAttribute('aria-expanded', targetExpanded);
				});
			}
			if (null === allControllers) {
				target.setAttribute('aria-expanded', targetExpanded);
			}
		}
	};

	// Set aria-hidden attributes based on browser width.
	setAriaHidden = () => {
		// if element is hidden, set aria-hidden, unless that elements controller is visible.
		const ariaElements = document.querySelectorAll('[aria-label]') as NodeListOf<HTMLElement>;
		ariaElements.forEach((el) => {
			if (this.elIsHidden(el)) {
				const controller = this.elHasController(el);
				if (controller) {
					el.setAttribute('aria-expanded', 'false');
					controller.setAttribute('aria-expanded', 'false');
				} else {
					el.setAttribute('aria-hidden', 'true');
				}
			} else {
				el.removeAttribute('aria-hidden');
			}
		});

		// if control element is hidden and controlled element is not hidden, remove aria-expanded from controlled element, otherwise apply the same value to the controlled element.
		const ariaControls = document.querySelectorAll(
			'[aria-controls]'
		) as NodeListOf<HTMLElement>;
		ariaControls.forEach((el) => {
			if (el.classList.contains('exclude-aria-handler')) {
				return;
			}
			const controlId = el.getAttribute('aria-controls');
			if (controlId) {
				const controlledElement = document.getElementById(controlId);
				if (controlledElement) {
					if (this.elIsHidden(el) && null !== controlledElement.offsetParent) {
						controlledElement.removeAttribute('aria-expanded');
					} else {
						const expanded = el.getAttribute('aria-expanded');
						if (expanded) {
							controlledElement.setAttribute('aria-expanded', expanded);
						}
					}
				}
			}
		});
	};

	// Trap focus within a provided element.
	trapFocus = (el: HTMLElement) => {
		const focusableEls = el.querySelectorAll(
			'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'
		);
		const firstFocusableEl = focusableEls[0] as HTMLElement;
		const lastFocusableEl = focusableEls[focusableEls.length - 1] as HTMLElement;

		el.addEventListener('keydown', function (e) {
			const isTabPressed = e.key === 'Tab';

			if (!isTabPressed) {
				return;
			}

			if (e.shiftKey) {
				// shift + tab on first
				// eslint-disable-next-line @wordpress/no-global-active-element
				if (document.activeElement === firstFocusableEl) {
					lastFocusableEl.focus();
					e.preventDefault();
				}
				// eslint-disable-next-line @wordpress/no-global-active-element
			} else if (document.activeElement === lastFocusableEl) {
				// tab on last
				firstFocusableEl.focus();
				e.preventDefault();
			}
		});
	};

	// Return an elements controller or false.
	elHasController = (el: HTMLElement) => {
		const thisId = el.getAttribute('id');
		const ariaControls = document.querySelector(
			'[aria-controls="' + thisId + '"]'
		) as HTMLElement | null;
		return ariaControls ? ariaControls : false;
	};

	// Return if element is hidden.
	elIsHidden(el: HTMLElement) {
		const style = window.getComputedStyle(el);
		return 'none' === style.display;
	}
}

export default new AriaHandler();
